import { buildApiUrl, fetchJSON, validateRequiredParams } from './crmApi';

export interface ContactInput {
  organization_id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string; // Legacy field, will migrate to mobile_phone
  mobile_phone?: string; // Primary phone number
  business_phone?: string; // Business landline
  business_phone_ext?: string; // Extension for business phone
  home_phone?: string; // Home phone
  other_phone?: string; // Other/backup phone
  fax?: string; // Fax number
  job_title?: string;
  department?: string;
  company_id?: string | null;
  persona?: string;
  lifecycle_stage?: string;
  lead_status?: string;
  lead_source?: string;
  linkedin_url?: string;
  region?: string;
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_postal_code?: string;
  address_country?: string;
  assistant_name?: string;
  assistant_phone?: string;
  birthdate?: string;
  notes?: string;
  tags?: string[];
  owner_user_id?: string;
}

export interface Contact extends ContactInput {
  id: string;
  owner_user_id: string;
  created_at: string;
  updated_at: string;
  lead_score?: number;
  enrichment_confidence?: number;
  enrichment_sources?: string[];
  enriched_at?: string;
}

export interface ListContactsParams {
  organization_id: string;
  search?: string;
  company_id?: string;
  limit?: number;
  offset?: number;
  persona?: string;
  lifecycle_stage?: string;
  lead_status?: string;
  region?: string;
  owner_user_id?: string;
}

const contactsService = {
  async list(params: ListContactsParams): Promise<{ data: Contact[]; count: number | null }>{
    validateRequiredParams(params, ['organization_id'], {
      service: 'contactsService.list'
    });
    const url = buildApiUrl('/api/crm/contacts', params as any);
    return fetchJSON(url, {
      description: 'List contacts',
      context: { params }
    });
  },
  async get(id: string, organization_id: string): Promise<{ data: Contact | null }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'contactsService.get'
    });
    const url = buildApiUrl(`/api/crm/contacts/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      description: 'Get contact',
      context: { id, organization_id }
    });
  },
  async create(payload: ContactInput): Promise<{ data: Contact }>{
    validateRequiredParams(payload, ['organization_id'], {
      service: 'contactsService.create'
    });
    const url = buildApiUrl('/api/crm/contacts');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create contact',
      context: { organization_id: payload.organization_id }
    });
  },
  async update(id: string, payload: ContactInput): Promise<{ data: Contact }>{
    validateRequiredParams({ id, ...payload }, ['id', 'organization_id'], {
      service: 'contactsService.update'
    });
    const url = buildApiUrl(`/api/crm/contacts/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: { ...payload, id },
      description: 'Update contact',
      context: { id, organization_id: payload.organization_id }
    });
  },
  async remove(id: string, organization_id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'contactsService.remove'
    });
    const url = buildApiUrl(`/api/crm/contacts/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete contact',
      context: { id, organization_id }
    });
  }
};

export default contactsService;
